import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./authForm.module.css";
import { AuthStepper } from "./AuthStepper";

const baseURL = process.env.REACT_APP_BACKEND_API_URL;

interface LocationState {
  email?: string;
}

const ConfirmSignup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  // Prefer email from navigation state; allow manual input if missing
  const [email, setEmail] = useState(state?.email ?? "");
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<"idle" | "verify">("idle");

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading("verify");
    try {
      await axios.post(`${baseURL}api/auth/confirm`, {
        email,
        code: verificationCode,
      });

      navigate("/login");
    } catch (err: any) {
      setError(err.response?.data?.message || "Verification failed.");
    } finally {
      setLoading("idle");
    }
  };

  const disableVerify = !verificationCode || !email || loading === "verify";

  return (
    <div className={styles.pane}>
      <AuthStepper current={2} />

      <header className={styles.head}>
        <h2 className={styles.title}>Verify your email</h2>
        <p className={styles.lead}>
          Enter the six-digit code we sent to{" "}
          <strong>{email || "your email"}</strong>.
        </p>
      </header>

      <form onSubmit={handleVerify} className={styles.form} noValidate>
        {error && (
          <p className="ui-banner ui-banner--error" role="alert">
            {error}
          </p>
        )}

        {/* If email is missing from state, allow editing */}
        {!state?.email && (
          <div className="ui-field">
            <label className="ui-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              name="email"
              className="ui-input"
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        )}

        <div className="ui-field">
          <label className="ui-label" htmlFor="code">
            Verification code
          </label>
          <input
            id="code"
            name="code"
            className={`ui-input ${styles.code}`}
            type="text"
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            required
            placeholder="000000"
            value={verificationCode}
            onChange={(e) =>
              setVerificationCode(
                e.target.value.replace(/[^0-9]/g, "").slice(0, 6)
              )
            }
          />
          <p className="ui-help">
            Check your spam folder if it has not arrived after a minute.
          </p>
        </div>

        <button
          type="submit"
          className={`ui-btn ui-btn--lg ui-btn--full ${styles.submit}`}
          disabled={disableVerify}
          aria-busy={loading === "verify"}
        >
          {loading === "verify" ? "Verifying..." : "Verify email"}
        </button>
      </form>

      <div className={styles.links}>
        <p>
          Wrong email address?{" "}
          <Link to="/signup" className="ui-link">
            Go back to signup
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ConfirmSignup;
